import { useMemo, useRef, useState } from "react";
import OnlineOrderValuesModel from "../../../../services/models/formModels/onlineOrderValues.model";
import CustomText from "../../../atomic/CustomText";
import toastMessages from "../../../../services/utils/helpers/toastMessage";
import MultipleFileUploader from "../../MultipleFileUploader";
import dataConstants from "../../../../constants/dataConstants";
import FileIcon from "../../../../assets/svg/FileIcon";
import useGetPresignedUrl from "../../../../services/utils/custom hooks/Documents/useGetPresignedUrl";
import { normalizeUploadFile } from "../../../../services/utils/helpers/fileNameNormalizer";

function getFileNameFromPath(path: string): string {
  const safePath = decodeURIComponent(path ?? "").split("?")[0];
  const segments = safePath.split("/").filter(Boolean);
  return segments[segments.length - 1] || "document";
}

function getPresignedRequestFileName(path: string): string {
  const fileName = getFileNameFromPath(path);
  const lastDotIndex = fileName.lastIndexOf(".");
  if (lastDotIndex <= 0) return fileName;

  const base = fileName.slice(0, lastDotIndex);
  const extension = fileName.slice(lastDotIndex);
  const normalizedBase = base.replace(/(_\d{10,})_\d{10,}$/, "$1");

  return `${normalizedBase}${extension}`;
}

function normalizeReviewDocuments(value: any): any {
  if (!value) return value;

  if (Array.isArray(value)) {
    return value
      .filter(Boolean)
      .map((item) => {
        if (typeof item === "string") {
          return {
            url: item,
            filename: getDisplayFileName(item),
          };
        }
        return item;
      });
  }

  if (typeof value === "string") {
    return [
      {
        url: value,
        filename: getDisplayFileName(value),
      },
    ];
  }

  return value;
}

const documentLabelByKey: Record<string, string> = {
  patientInfoDemographicSheet: "Patient Info/Demographics sheet",
  insuranceCard: "Insurance Card",
  pathologyReport: "Pathology Report",
  endoscopyReport: "Endoscopy Report",
  currentPrescriptionMedications: "Current Prescription Medications",
  clinicNotes: "Clinic Notes",
  requisitionForm: "Requisition Form",
  visitNotes: "Visit Note",
  lomn: "LOMN",
  other: "Other",
};
const viewUrlStorageKey = "supportingDocumentsViewUrlMap";
const displayNameStorageKey = "supportingDocumentsDisplayNameMap";
const fileSizeStorageKey = "supportingDocumentsFileSizeMap";
const classificationLabelsStorageKey = "supportingDocumentsClassificationLabelsMap";

interface ReviewDocumentItem {
  id: string;
  labels: string[];
  filename: string;
  url?: string;
  sizeInBytes?: number;
}

function getNormalizedPath(path: string): string {
  return decodeURIComponent(path).replace(/^\/+/, "").toLowerCase();
}

function getStoredViewUrlMap(): Record<string, string> {
  try {
    const raw = sessionStorage.getItem(viewUrlStorageKey);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

function saveStoredViewUrl(path: string, url: string): void {
  if (!path || !url) return;
  const existing = getStoredViewUrlMap();
  existing[path] = url;
  sessionStorage.setItem(viewUrlStorageKey, JSON.stringify(existing));
}

function stripTimestampSuffix(fileName: string): string {
  const lastDotIndex = fileName.lastIndexOf(".");
  if (lastDotIndex <= 0) return fileName;
  const base = fileName.slice(0, lastDotIndex);
  const extension = fileName.slice(lastDotIndex);
  return `${base.replace(/_\d{10,}$/, "")}${extension}`;
}

function getStoredDisplayNameMap(): Record<string, string> {
  try {
    const raw = sessionStorage.getItem(displayNameStorageKey);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

function getDisplayFileName(path?: string, fallback?: string): string {
  if (path) {
    const stored = getStoredDisplayNameMap()[path];
    if (stored) return stored;
    return stripTimestampSuffix(getFileNameFromPath(path));
  }
  if (fallback) return stripTimestampSuffix(fallback);
  return "document";
}

function getStoredFileSizeMap(): Record<string, number> {
  try {
    const raw = sessionStorage.getItem(fileSizeStorageKey);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

function getDisplayFileSize(path?: string, fallback?: number): number {
  if (typeof fallback === "number" && fallback >= 0) return fallback;
  if (path) {
    const stored = getStoredFileSizeMap()[path];
    if (typeof stored === "number" && stored >= 0) return stored;
  }
  return 0;
}

function getStoredClassificationLabelsMap(): Record<string, string[]> {
  try {
    const raw = sessionStorage.getItem(classificationLabelsStorageKey);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

function getStoredClassificationLabelsForPath(
  path: string,
  storedLabelsMap: Record<string, string[]>
): string[] {
  const normalizedPath = getNormalizedPath(path);
  const directMatch = storedLabelsMap[normalizedPath];
  if (directMatch) return directMatch;

  const matchedKey = Object.keys(storedLabelsMap).find((storedPath) => {
    const normalizedStoredPath = getNormalizedPath(storedPath);
    return (
      normalizedStoredPath === normalizedPath ||
      normalizedStoredPath.endsWith(normalizedPath) ||
      normalizedPath.endsWith(normalizedStoredPath)
    );
  });

  return matchedKey ? storedLabelsMap[matchedKey] ?? [] : [];
}

function TransmitDoucments({
  values,
  setFieldValue,
}: {
  values: OnlineOrderValuesModel;
  setFieldValue?: any;
}) {
  const inputRef = useRef<any>();

  function handleRemove(fileIndex: number) {
    return () => {
      setFieldValue(
        "transitDocuments",
        values.transitDocuments.filter((document, index) => index !== fileIndex)
      );
    };
  }

  const validateFile = (file: any): boolean => {
    let sizeInMB = null;
    sizeInMB = file.size / 1024 / 1024;

    if (values.transitDocuments.length > 0) {
      if (
        values.transitDocuments.some((item: any) => item.name === file.name)
      ) {
        alert("File already added");
        return false;
      }
    }

    function getExt(filename: string): any {
      const ext = filename.split(".").pop();
      if (ext === filename) return "";
      return ext;
    }

    if (!dataConstants.validTypes.includes(getExt(file.name)?.toLowerCase())) {
      toastMessages.error(`Invalid file format`);
      return false;
    }

    if (sizeInMB > 55) {
      alert("File size limit is being exceeded");
      return false;
    }

    return true;
  };

  function handleFiles(event: any): void {
    let inputFiles = event.target.files;
    let temp: any[] = values.transitDocuments;

    for (const element of inputFiles) {
      const normalizedFile = normalizeUploadFile(element);
      if (validateFile(normalizedFile)) {
        temp.push(normalizedFile);
      }
    }

    setFieldValue("transitDocuments", temp);
  }

  async function handleDrop(event: any) {
    event.preventDefault();
    event.stopPropagation();
    let inputFiles = event.dataTransfer.files;
    let temp: any[] = [];

    if (values.transitDocuments.length > 0) {
      temp = [...values.transitDocuments];
    }

    for (const element of inputFiles) {
      const normalizedFile = normalizeUploadFile(element);
      if (validateFile(normalizedFile)) {
        temp.push(normalizedFile);
      }
    }

    setFieldValue("transitDocuments", temp);
  }

  return (
    <div>
      <CustomText
        text="Upload and transmit more documents"
        fontWeight="font-semibold"
      />
      <div className="my-3">
        <MultipleFileUploader
          handleDrop={handleDrop}
          handleRemove={handleRemove}
          files={values.transitDocuments}
          inputRef={inputRef}
        />
      </div>
      <input
        type="file"
        id="file"
        multiple
        accept=".png, .jpeg, .pdf, .doc/*"
        ref={inputRef}
        onChange={handleFiles}
        style={{ display: "none" }}
      />
      <CustomText text="Signature Allowed" fontWeight="font-semibold" />
      <CustomText
        text={`${values?.isPhysicianSignPermission ? "Yes" : "No"}`}
        fontWeight="font-normal"
      />
    </div>
  );
}

function SupportingDocumentsReview({
  values,
  setFieldValue,
}: {
  values: OnlineOrderValuesModel;
  setFieldValue?: any;
}) {
  const getPresignedUrl = useGetPresignedUrl();
  const [viewUrlCache, setViewUrlCache] = useState<Record<string, string>>({});

  const supportingDocFiles = useMemo(() => {
    const storedClassificationLabels = getStoredClassificationLabelsMap();
    const rawSupportingDocFiles: Record<string, any> = {
      patientInfoDemographicSheet: values.supportingDocuments.demographicsSheet,
      insuranceCard: values.supportingDocuments.insuranceCard,
      pathologyReport: values.supportingDocuments.pathologyReport,
      endoscopyReport: values.supportingDocuments.endoscopyReport,
      currentPrescriptionMedications:
        values.supportingDocuments.currentPrescriptionMedications,
      clinicNotes: values.supportingDocuments.clinicNotes,
      requisitionForm: (values.supportingDocuments as any).requisitionForm,
      visitNotes: (values.supportingDocuments as any).visitNotes,
      lomn: (values.supportingDocuments as any).lomn,
      other: (values.supportingDocuments as any).other,
    };

    const filesByPath = new Map<string, ReviewDocumentItem>();

    Object.keys(rawSupportingDocFiles).forEach((key) => {
      const normalizedFiles = normalizeReviewDocuments(rawSupportingDocFiles[key]);
      if (!Array.isArray(normalizedFiles) || normalizedFiles.length === 0) return;

      normalizedFiles.forEach((file: any, index: number) => {
        const fileUrl =
          typeof file === "string"
            ? file
            : typeof file?.url === "string"
            ? file.url
            : undefined;
        const fileName =
          typeof file?.filename === "string" && file.filename
            ? getDisplayFileName(fileUrl, file.filename)
            : getDisplayFileName(fileUrl);
        const label = documentLabelByKey[key] ?? key;
        const dedupeKey = fileUrl ? getNormalizedPath(fileUrl) : `${fileName}-${index}`;
        const existing = filesByPath.get(dedupeKey);
        const persistedLabels = fileUrl
          ? getStoredClassificationLabelsForPath(
              fileUrl,
              storedClassificationLabels
            )
          : [];
        const mergedLabels = Array.from(
          new Set([...(existing?.labels ?? []), ...persistedLabels, label])
        );

        filesByPath.set(dedupeKey, {
          id: existing?.id ?? `${key}-${index}-${fileUrl ?? fileName}`,
          labels: mergedLabels,
          filename: fileName,
          url: fileUrl ?? existing?.url,
          sizeInBytes:
            getDisplayFileSize(fileUrl, file?.size) || existing?.sizeInBytes || 0,
        });
      });
    });

    return Array.from(filesByPath.values()).filter((item) => !!item.filename);
  }, [values.testTypeId, values.supportingDocuments]);

  const handleViewFile = async (filePath?: string) => {
    if (!filePath) return;
    const storedViewUrl = getStoredViewUrlMap()[filePath];
    if (viewUrlCache[filePath] || storedViewUrl) {
      const resolvedUrl = viewUrlCache[filePath] ?? storedViewUrl;
      if (resolvedUrl && !viewUrlCache[filePath]) {
        setViewUrlCache((prevState) => ({
          ...prevState,
          [filePath]: resolvedUrl,
        }));
      }
      window.open(resolvedUrl, "_blank", "noopener,noreferrer");
      return;
    }

    try {
      const fileName = getPresignedRequestFileName(filePath);
      const presignedUrlResponse = await getPresignedUrl.mutateAsync(fileName);
      const presignedUrl = presignedUrlResponse?.data?.url;

      if (presignedUrl) {
        saveStoredViewUrl(filePath, presignedUrl);
        setViewUrlCache((prevState) => ({
          ...prevState,
          [filePath]: presignedUrl,
        }));
        window.open(presignedUrl, "_blank", "noopener,noreferrer");
        return;
      }

      toastMessages.error("Unable to open file");
    } catch (error: any) {
      toastMessages.error(
        error?.response?.data?.message ?? error?.message ?? "Unable to open file"
      );
    }
  };

  return (
    <div>
      <CustomText
        text="Supporting documents"
        fontWeight="font-medium"
        className="reviewCardHeading my-5"
      />
      <div className="reviewCard">
        <CustomText
          text="Upload documents"
          fontWeight="font-medium"
          className="mt-5"
        />
        {supportingDocFiles.length > 0 ? (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            {supportingDocFiles.map((document) => (
              <div
                key={document.id}
                className="border border-roundedInputBorder rounded-lg bg-white p-3 shadow-sm"
              >
                <div className="flex items-start gap-2">
                  <div className="flex h-6 w-6 shrink-0 items-center justify-center">
                    <FileIcon className="h-6 w-6 shrink-0" />
                  </div>
                  <div className="min-w-0">
                    <CustomText
                      text={document.filename}
                      fontWeight="font-medium"
                      fontSize="text-sm"
                      className="text-royalBlue truncate"
                    />
                    <CustomText
                      text={`${Math.round((document.sizeInBytes ?? 0) / 1024)} kb`}
                      fontWeight="font-normal"
                      fontSize="text-xs"
                      className="text-darkGray"
                    />
                  </div>
                </div>

                <div className="mt-3 flex items-center justify-between">
                  <CustomText
                    text="Uploaded"
                    fontWeight="font-medium"
                    fontSize="text-xs"
                    className="text-darkGray"
                  />
                  <div className="flex flex-wrap items-center justify-end gap-1">
                    {document.labels.map((label) => (
                      <div
                        key={`${document.id}-${label}`}
                        className="inline-flex items-center rounded-full bg-lightBlue px-2 py-1"
                      >
                        <span className="mr-1 inline-block h-2 w-2 rounded-full bg-primary" />
                        <CustomText
                          text={label}
                          fontWeight="font-medium"
                          fontSize="text-xs"
                          className="text-royalBlue"
                        />
                      </div>
                    ))}
                  </div>
                </div>
                {document.url && (
                  <div className="mt-2">
                    <button
                      type="button"
                      className="text-xs text-activeLink font-medium"
                      onClick={() => {
                        void handleViewFile(document.url);
                      }}
                    >
                      View file
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : null}
        <TransmitDoucments values={values} setFieldValue={setFieldValue} />
      </div>
    </div>
  );
}
export default SupportingDocumentsReview;
